/**
 * Per-source-type visual identity (icon/logo + accent colour) for the catalog
 * and connection cards. Keyed by `source_type`.
 *
 * Where a vendor logo is provided (logos/), `SourceTile` renders it on a
 * neutral tile; otherwise the `Icon` fallback renders on a colour-tinted tile.
 * The accent `color` drives category badges + the Connect button regardless.
 * Logos are trademarks of their respective owners, used for identification
 * only (ADR-0010).
 */

import type { ComponentType } from "react";
import {
  IconBrandAws,
  IconBrandAzure,
  IconBrandOffice,
  IconDatabase,
  IconFileCertificate,
  IconShieldCheck,
  IconSparkles,
} from "@tabler/icons-react";
import awsS3Logo from "./logos/aws-s3.svg";
import azureBlobLogo from "./logos/azure-blob.png";
import sharepointLogo from "./logos/SharePoint.svg";
import veevaLogo from "./logos/veeva.png";

type IconComponent = ComponentType<{ size?: number | string }>;

export interface SourceVisual {
  /** Fallback glyph when no vendor logo is available. */
  Icon: IconComponent;
  /** Mantine theme colour key used for the icon tile + accents. */
  color: string;
  /** Bundled vendor logo URL, when available. */
  logo?: string;
}

const VISUALS: Record<string, SourceVisual> = {
  veeva: { Icon: IconFileCertificate, color: "teal", logo: veevaLogo },
  blob: { Icon: IconBrandAzure, color: "blue", logo: azureBlobLogo },
  // No bundled Foundry mark yet — the semantic glyph fallback applies
  // (ADR-0010: a logo-absent source type falls back to an icon).
  ai_foundry: { Icon: IconSparkles, color: "grape" },
  azure_devops: { Icon: IconBrandAzure, color: "blue" },
  // One tile for four engines (ADR-0022), so the glyph is the generic one
  // rather than any vendor's mark.
  database: { Icon: IconDatabase, color: "indigo" },
  s3: { Icon: IconBrandAws, color: "orange", logo: awsS3Logo },
  sharepoint: { Icon: IconBrandOffice, color: "cyan", logo: sharepointLogo },
  cara: { Icon: IconShieldCheck, color: "violet" },
};

const FALLBACK: SourceVisual = { Icon: IconDatabase, color: "gray" };

export function sourceVisual(sourceType: string): SourceVisual {
  return VISUALS[sourceType] ?? FALLBACK;
}
